import React, { useState } from "react";
import AgentsCrumb from "./AgentsCrumb";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { agentsData } from "Stores/agentsData";
import AgentSelect from "./AgentSelect";
import { defaultTimezone } from "./DayOfWeekField";
import SequencePickerModal from "./SequencePickerModal";

const CreateReplyRouter = observer(() => {
  const history = useHistory();
  const [name, setName] = useState("");
  const [channel, setChannel] = useState("email"); // email | linkedin
  const [positiveAction, setPositiveAction] = useState("task"); // task | enroll | notify
  const [sequenceId, setSequenceId] = useState("");
  const [sequenceName, setSequenceName] = useState("");
  const [seqModalOpen, setSeqModalOpen] = useState(false);
  const [stopOnNegative, setStopOnNegative] = useState(true);
  const [notifyMode, setNotifyMode] = useState("ON_FAILURE");
  const [saving, setSaving] = useState(false);
  const timezone = defaultTimezone();

  const needsSequence = positiveAction === "enroll";
  const canSubmit = !needsSequence || !!sequenceId;
  const channelLabel = channel === "linkedin" ? "on LinkedIn" : "to an email";

  const buildPayload = () => ({
    name: name.trim() || "Reply router",
    channel,
    positiveAction,
    positiveSequenceId: needsSequence ? sequenceId : undefined,
    stopOnNegative,
    notifyMode,
    timezone
  });

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const agent = await agentsData.createReplyRouter(buildPayload());
    setSaving(false);
    if (agent && agent.id) history.push(`/agents/${agent.id}`);
  };

  const customize = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const draft = await agentsData.composeTemplate("reply-router", buildPayload());
    setSaving(false);
    if (draft) { agentsData.stageDraft(draft); history.push("/agents/new/custom"); }
  };

  const posText = positiveAction === "enroll"
    ? <span>Enrolls them into <b>{sequenceName || "a sequence"}</b></span>
    : positiveAction === "task"
      ? <span>Creates a call task for you to follow up</span>
      : <span>Notifies you</span>;
  const step = node => (<li><span className="ck">✓</span><span>{node}</span></li>);

  return (
    <div className="ag-page">
      <div className="ag-page__head">
        <div>
          <AgentsCrumb items={[{ label: "Hire an agent", to: "/agents/new" }, { label: "Set up" }]} />
          <h1 className="ag-page__title">Hire · Reply router</h1>
          <p className="ag-page__sub">When a contact replies, route them by sentiment, act on positive replies, stop outreach on negative ones. Uses the reply sentiment we already detect.</p>
        </div>
      </div>

      <div className="ag-setup">
        <div className="ag-card-panel">
          <div className="ag-fsec">Trigger</div>
          <label className="ag-field">
            <span className="ag-field__label">When a contact replies…</span>
            <AgentSelect value={channel} onChange={setChannel} options={[
              { value: "email", label: "…to an email" },
              { value: "linkedin", label: "…on LinkedIn" }
            ]} />
          </label>
          <label className="ag-field">
            <span className="ag-field__label">Agent name</span>
            <input className="ag-input" value={name} placeholder="Reply router" onChange={e => setName(e.target.value)} />
          </label>

          <div className="ag-fsec">Positive reply</div>
          <label className="ag-field">
            <span className="ag-field__label">When the reply is positive</span>
            <AgentSelect value={positiveAction} onChange={setPositiveAction} options={[
              { value: "task", label: "Create a call task for me" },
              { value: "enroll", label: "Enroll them in a sequence" },
              { value: "notify", label: "Just notify me" }
            ]} />
          </label>
          {needsSequence && (
            <label className="ag-field">
              <span className="ag-field__label">Sequence<b className="ag-req">required</b></span>
              <button type="button" className="ag-select__btn" onClick={() => setSeqModalOpen(true)}>
                <span className={sequenceName ? "" : "ag-select__ph"}>{sequenceName || "Select a sequence…"}</span>
                <svg className="ag-select__chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
              </button>
            </label>
          )}

          <div className="ag-fsec">Negative reply</div>
          <label className="ag-check" style={{ display: "flex", alignItems: "center", gap: 8, margin: "2px 0 6px" }}>
            <input type="checkbox" checked={stopOnNegative} onChange={e => setStopOnNegative(e.target.checked)} />
            <span>Stop their sequence on a negative reply</span>
          </label>
          <span className="ag-hint">Neutral or unclassified replies just notify you, you can add steps to that lane with “Customize steps”.</span>

          <div className="ag-fsec">Notifications</div>
          <label className="ag-field">
            <span className="ag-field__label">Notify me</span>
            <AgentSelect value={notifyMode} onChange={setNotifyMode} options={[
              { value: "ON_FAILURE", label: "Only when a run fails" },
              { value: "DAILY_DIGEST", label: "Daily digest, one summary a day" },
              { value: "OFF", label: "Off, don't email me" }
            ]} />
          </label>
        </div>

        <div className="ag-rail">
          <div className="ag-summary">
            <h4>⚡ What this agent does</h4>
            <ul className="ag-slist">
              {step(<span>Runs when a contact replies <b>{channelLabel}</b></span>)}
              {step(<span>Reads the reply’s sentiment (positive / negative / neutral)</span>)}
              {step(<span><b>Positive →</b> {posText}</span>)}
              {stopOnNegative && step(<span><b>Negative →</b> stops their sequence</span>)}
              {step(<span><b>Neutral →</b> {notifyMode !== "OFF" ? "notifies you" : "no action"}</span>)}
            </ul>
          </div>
          <button className="ag-btn ag-btn--primary" style={{ justifyContent: "center" }} disabled={!canSubmit || saving} onClick={submit}><i className="material-icons-outlined">check</i>
            {saving ? "Creating…" : "Create agent"}
          </button>
          <button className="ag-btn" style={{ justifyContent: "center" }} disabled={!canSubmit || saving} onClick={customize}><i className="material-icons-outlined">tune</i>Customize steps</button>
          <button className="ag-btn" style={{ justifyContent: "center" }} onClick={() => history.push("/agents")}><i className="material-icons-outlined">close</i>Cancel</button>
          {!canSubmit && <p className="ag-hint" style={{ textAlign: "center" }}>Pick a sequence to continue.</p>}
        </div>
      </div>

      <SequencePickerModal
        open={seqModalOpen}
        currentId={sequenceId}
        onSelect={s => { setSequenceId(s.id); setSequenceName(s.name); }}
        onClose={() => setSeqModalOpen(false)}
      />
    </div>
  );
});

export default CreateReplyRouter;
